import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useAudio } from '@context/AudioContext';

interface BackgroundVisualsProps {
  intensity: number; // Value between 0 and 1
}

const VisualsContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  overflow: hidden;
  pointer-events: none;
`;

const Canvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.45;
  filter: none;
`;

const BackgroundVisuals: React.FC<BackgroundVisualsProps> = ({ intensity: _ = 0.5 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { isPlaying } = useAudio();
  
  // Animation frame reference
  const requestRef = useRef<number>(0);
  
  // Brighter, more saturated 60s colors for better visibility
  const colors = [
    '#FF3B30', // bright red
    '#FFC300', // vibrant gold
    '#FF7043', // bright orange
    '#C25E00', // deep amber
    '#FF5722'  // vibrant terracotta
  ];
  
  // Simple render function
  const render = (timestamp: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const { width, height } = canvas;
    
    // Clear the canvas completely each frame to prevent accumulation
    ctx.clearRect(0, 0, width, height);
    
    // Reset with solid black background
    ctx.fillStyle = 'black';
    ctx.fillRect(0, 0, width, height);
    
    // Use difference blend mode for psychedelic effect
    ctx.globalCompositeOperation = 'difference';
    
    // More noticeable animation that increases when playing music
    const activityLevel = isPlaying ? 1.0 : 0.7;
    
    // Slower, more viscous lava lamp movement
    const time = timestamp * 0.0008; // Slightly slower for more lava lamp feel
    
    // Draw a few large blobs that move slowly
    for (let i = 0; i < 5; i++) {
      // Initial positions more spread apart with golden ratio distribution
      const goldenAngle = i * 2.39996; // Golden angle for better distribution
      const xBase = 0.2 + (i / 5) * 0.6; // Horizontal spread from 0.2 to 0.8
      const yBase = 0.3 + ((i * 1.618) % 5) / 5 * 0.4; // Vertical spread with golden ratio
      
      // Smoother sine waves for more lava lamp-like motion
      const xCenter = width * (xBase + 0.15 * Math.sin(time * 0.04 + goldenAngle));
      const yCenter = height * (yBase + 0.15 * Math.sin(time * 0.03 + goldenAngle * 0.7));
      
      // Draw several blended circles to create organic shapes
      for (let j = 0; j < 3; j++) {
        const radius = (120 + i * 40 + j * 30) * activityLevel;
        const offset = 40 * Math.sin(time * 0.2 + i + j);
        
        // Get color with correct alpha for blending
        const color = colors[(i + j) % colors.length];
        
        // Create radial gradient for soft edges
        const gradient = ctx.createRadialGradient(
          xCenter + offset, yCenter, 0,
          xCenter + offset, yCenter, radius
        );
        
        // More visible colors but still with soft edges
        gradient.addColorStop(0, color + '99'); // 0.6 opacity
        gradient.addColorStop(0.7, color + '66'); // 0.4 opacity
        gradient.addColorStop(1, color + '00'); // Transparent
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(xCenter + offset, yCenter, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    // Schedule next frame
    requestRef.current = requestAnimationFrame(render);
  };
  
  // Initialize canvas and start animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Set canvas dimensions
    const updateCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    // Update canvas size initially and on resize
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    
    // Start animation loop
    requestRef.current = requestAnimationFrame(render);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      cancelAnimationFrame(requestRef.current!);
    };
  }, []);
  
  return (
    <VisualsContainer>
      <Canvas ref={canvasRef} />
    </VisualsContainer>
  );
};

export default BackgroundVisuals;